import type { UserWorkspaceRole } from "@common/userWorkspaceRoles";
import {
    isCheckboxCapableEvent,
    isUndoEligibleEvent,
    type CoaTemplateActivityEventRef,
    type CoaTemplateActivityEventResult,
} from "./coaTemplateActivityEvents";
import {
    getBlockedUndoReasonCategory,
    getEventBlockedUndoReasonCategory,
    type CoaUndoBlockedReasonCategory,
} from "./coaUndoBlockedReason";

export interface CoaUndoCommand {
    // Total selected entity events, regardless of eligibility. Drives the
    // "Undo selected (N)" CTA label.
    selectedEntityCount: number;
    // The CTA is only enabled when every selected event is backend-eligible and
    // no mutation is in flight.
    canConfirm: boolean;
    // Names of the currently-selected blocked entities, in rail order, for the
    // disabled-Undo tooltip. Empty when the CTA is enabled.
    blockedEntityNames: string[];
    blockedReasonCategory: CoaUndoBlockedReasonCategory | null;
    // Sum of eligible actions across selected eligible events, for the modal.
    eligibleActionCount: number;
}

interface EntityNameEntry {
    entityId: number;
    name: string;
}

// Pure command model for the Applied undo command island. Returns null when no
// entity is checkbox-capable (nothing to undo), which tells the drawer to fall
// back to a reverted/close-only island.
export function buildCoaUndoCommand({
    events,
    selectedEventRefs,
    entries,
    workspaceRolesById,
    isUndoPending,
}: {
    events: CoaTemplateActivityEventResult[];
    selectedEventRefs: CoaTemplateActivityEventRef[];
    entries: EntityNameEntry[];
    workspaceRolesById: ReadonlyMap<string, UserWorkspaceRole>;
    isUndoPending: boolean;
}): CoaUndoCommand | null {
    const hasCheckboxCapableEntity = events.some((event) =>
        isCheckboxCapableEvent({ event, workspaceRolesById }),
    );
    if (!hasCheckboxCapableEntity) {
        return null;
    }

    const eventById = new Map(
        events.map((event) => [event.eventRef.eventId, event]),
    );
    const selectedEvents = selectedEventRefs.map((eventRef) => ({
        eventRef,
        event: eventById.get(eventRef.eventId),
    }));

    const blockedEvents = selectedEvents.filter(
        ({ event }) => !event || !isUndoEligibleEvent(event),
    );
    const blockedEntityIds = new Set(
        blockedEvents.map(({ eventRef }) => eventRef.entityId),
    );
    const blockedEntityNames = entries
        .filter((entry) => blockedEntityIds.has(entry.entityId))
        .map((entry) => entry.name);

    const eligibleActionCount = selectedEvents.reduce(
        (sum, { event }) =>
            sum +
            (event && isUndoEligibleEvent(event) && event.status === "loaded"
                ? event.eligibility.eligibleCount
                : 0),
        0,
    );

    const selectedEntityCount = selectedEventRefs.length;
    return {
        selectedEntityCount,
        canConfirm:
            selectedEntityCount > 0 &&
            blockedEntityIds.size === 0 &&
            !isUndoPending,
        blockedEntityNames,
        blockedReasonCategory:
            blockedEvents.length > 0
                ? getBlockedUndoReasonCategory(
                      blockedEvents.map(({ event }) =>
                          getEventBlockedUndoReasonCategory(event),
                      ),
                  )
                : null,
        eligibleActionCount,
    };
}
